import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import SelectorOptionInput from 'metadata/SelectorOptionInput';
import {FieldType, isNumericField, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARK} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

import {
  FilterLookupStepInfo,
  FilterLookupStepSpec,
  FilterType,
  FILTER_TYPE_TO_READABLE,
  LookupStepType,
} from './LookupConfigModalConstants';
import {filterOptionsForFieldType, SUPPORTED_FILTER_TYPES} from './LookupConfigUtils';

export interface FilterLookupStepFormErrors {
  schemaField: boolean;
  filterValue: boolean;
}

interface FilterValueFieldProps {
  disabled?: boolean;
  lookupStepInfo: FilterLookupStepInfo;
  onChange: (lookupInfo: FilterLookupStepInfo) => void;
}

function FilterValueField(props: FilterValueFieldProps): JSX.Element | null {
  function filterValueChange(value: string | number): void {
    props.onChange({
      ...props.lookupStepInfo,
      lookupStep: {
        ...props.lookupStepInfo.lookupStep,
        value,
      },
    });
  }

  if ([FilterType.ISNULL, FilterType.NOTNULL].includes(props.lookupStepInfo.lookupStep.filterType)) {
    return null;
  }
  const schemaField = props.lookupStepInfo.schemaField;
  if (schemaField && isNumericField(schemaField)) {
    return (
      <NumericInput
        isDisabled={props.disabled}
        placeholder="Enter value"
        value={props.lookupStepInfo.lookupStep.value}
        onChange={(e: NumericInputChangeEvent) => {
          if (e.target.numericValue != null) {
            filterValueChange(e.target.numericValue);
          }
        }}
      />
    );
  }
  if (schemaField && schemaField.fieldType === FieldType.SELECTOR && schemaField.schemaFieldSelectorId) {
    return (
      <SelectorOptionInput
        onChange={(tag) => {
          filterValueChange(tag.value);
        }}
        schemaFieldSelectorId={schemaField.schemaFieldSelectorId}
        shouldAllowMultiple={false}
        defaultValue={{
          schemaFieldSelectorOptionIds: [],
          value: props.lookupStepInfo.lookupStep.value?.toString(),
        }}
      />
    );
  }
  return (
    <TextInput
      disabled={props.disabled}
      value={props.lookupStepInfo.lookupStep.value}
      placeholder="Enter value"
      onChange={(e: InputChangeEvent) => {
        filterValueChange(e.target.value);
      }}
    />
  );
}

function convertValueToType(value: string | number | undefined, fieldType: FieldType): string | number {
  if (value == null) {
    return '';
  }
  if (fieldType === FieldType.INTEGER) {
    if (typeof value === 'string') {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? '' : parsed;
    }
    return Math.round(value);
  }
  if (fieldType === FieldType.FLOAT) {
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return Number.isNaN(parsed) ? '' : parsed;
    }
    return value;
  }
  return String(value);
}

interface FilterLookupStepFormProps {
  lookupStepInfo: FilterLookupStepInfo;
  onChange: (lookupInfo: FilterLookupStepInfo) => void;
  onRemove: () => void;
  disabled?: boolean;
  errors: FilterLookupStepFormErrors;
  fieldOptions: Array<SchemaField>;
}

export default function FilterLookupStepForm(props: FilterLookupStepFormProps): JSX.Element {
  const schemaField = props.lookupStepInfo.schemaField;
  return (
    <div className={css(styles.wrapper)}>
      <span className={css(styles.content)}>
        <div className={css(styles.innerWrapper)}>
          <Row>
            <Col md={6} className={css(styles.field)}>
              <SelectDropdown
                disabled={props.disabled}
                displayName={schemaField ? schemaField.displayName : 'Missing value'}
                alwaysDropDown={true}
                error={props.errors.schemaField ? 'Field deleted' : null}
                menu={
                  <SearchableMenu
                    selectedValue={props.lookupStepInfo.schemaField}
                    onSelect={(option: SchemaField) => {
                      const currFilterType = props.lookupStepInfo.lookupStep.filterType;
                      const newFilterTypes = filterOptionsForFieldType(option.fieldType);
                      const newFilterType = newFilterTypes.includes(currFilterType)
                        ? currFilterType
                        : newFilterTypes[0];
                      const newLookupStep: FilterLookupStepSpec = {
                        type: LookupStepType.FILTER,
                        schemaField: option.apiId,
                        filterType: newFilterType,
                      };
                      if (newFilterTypes.includes(FilterType.EQ)) {
                        newLookupStep.value = convertValueToType(
                          props.lookupStepInfo.lookupStep.value,
                          option.fieldType
                        );
                      }
                      props.onChange({
                        ...props.lookupStepInfo,
                        name: `Filter: ${option.displayName}`,
                        lookupStep: newLookupStep,
                        schemaField: option,
                      });
                    }}
                    optionDatas={props.fieldOptions.map((option) => ({
                      name: option.displayName,
                      value: option,
                    }))}
                    height={Menu.HEIGHTS.MEDIUM}
                    width={Menu.WIDTHS.FULL}
                    scrollable={true}
                  />
                }
              />
            </Col>
            <Col md={6} className={css(styles.field)}>
              <SelectDropdown
                disabled={props.disabled}
                alwaysDropDown={true}
                displayName={FILTER_TYPE_TO_READABLE[props.lookupStepInfo.lookupStep.filterType]}
                menu={
                  <Menu
                    value={props.lookupStepInfo.lookupStep.filterType}
                    height={Menu.HEIGHTS.MEDIUM}
                    width={Menu.WIDTHS.SMALL}
                    scrollable={true}
                  >
                    {(schemaField
                      ? filterOptionsForFieldType(schemaField.fieldType)
                      : SUPPORTED_FILTER_TYPES
                    ).map((filterType: FilterType, i) => (
                      <Option
                        key={i}
                        value={filterType}
                        onSelect={(filterType: FilterType) => {
                          const currLookupStep = props.lookupStepInfo.lookupStep;
                          const newLookupStep: FilterLookupStepSpec = [
                            FilterType.ISNULL,
                            FilterType.NOTNULL,
                          ].includes(filterType)
                            ? {
                                // Be sure not to include "value" when switching to isnull/notnull
                                type: LookupStepType.FILTER,
                                schemaField: currLookupStep.schemaField,
                                filterType,
                              }
                            : {
                                ...currLookupStep,
                                filterType,
                                value: currLookupStep.value ?? '',
                              };
                          props.onChange({
                            ...props.lookupStepInfo,
                            lookupStep: newLookupStep,
                          });
                        }}
                      >
                        {FILTER_TYPE_TO_READABLE[filterType]}
                      </Option>
                    ))}
                  </Menu>
                }
              />
            </Col>
          </Row>
          <Row>
            <Col md={12} className={css(styles.field)}>
              <FilterValueField
                disabled={props.disabled}
                lookupStepInfo={props.lookupStepInfo}
                onChange={props.onChange}
              />
              {props.errors.filterValue ? (
                <ErrorMessage error={`Filter value must be a number for ${schemaField!.displayName}`} />
              ) : null}
            </Col>
          </Row>
        </div>
      </span>
      <span>
        <BenchlingIcon className={css(styles.removeIcon)} icon="close" onClick={props.onRemove} />
      </span>
    </div>
  );
}

const styles = StyleSheet.create({
  field: {
    ...FONT_PROXIMA_13,
    marginBottom: '8px',
  },
  wrapper: {
    display: 'flex',
  },
  content: {
    flexGrow: 1,
    margin: -10,
    overflowX: 'hidden',
  },
  innerWrapper: {
    padding: 10,
  },
  removeIcon: {
    color: GRAY_DARK,
    cursor: 'pointer',
    fontSize: 10,
    margin: '9px 2px 9px 9px',
  },
});
